import { SubBranches } from '~/config'
import { Form } from '~/layers/forms/models'

export type FormConfig = Record<
  number,
  {
    navigation?: boolean
    forms: Form[]
    argantoInsuranceStage?: SiteApiSchema<'OportunidadEtapa'>
    showQuotation?: boolean
  }
>

/**
 * Makes a request to /formConfiguration and returns the form configuration object.
 * @returns {Promise<FormConfig>} A promise that resolves with the form configuration object.
 */
const getFormConfiguration = (): FormConfig => {
  const { LocalConfigurationStore } = stores()

  const { formConfig: configuredForm } = LocalConfigurationStore.value ?? {}

  if (!configuredForm) return {}

  return Object.entries(configuredForm).reduce((config: FormConfig, step) => {
    config[+step[0]] = step[1]
    return config
  }, {})
}

/**
 * Builds the form configuration object.
 * @returns {Promise<FormConfig>} A promise that resolves with the form configuration object.
 *
 * This function builds the form configuration object by setting up the steps and the forms
 * that will be shown in each step. The steps and forms are set up based on the RemoteConfigurationStore value.
 *
 * The function starts by setting up the initial steps, which are valid for all forms.
 * Then, it sets up the specialized forms of each product, based on the RemoteConfigurationStore value.
 *
 * Finally, it returns the form configuration object, which will be used by the stepper.
 */
const buildForm = (): FormConfig => {
  const steps: FormConfig = {}
  const { RemoteConfigurationStore } = stores()
  if (!RemoteConfigurationStore.value) return steps

  if (RemoteConfigurationStore?.value?.initBy === 'ByProducto') {
    let coverageCriteriaForm: Form | undefined
    switch (RemoteConfigurationStore?.value?.ramoEnum) {
      case 'Viajes':
        coverageCriteriaForm = Form.travel
        break
    }

    const coverageStepForms = coverageCriteriaForm
      ? [coverageCriteriaForm, Form.coverage]
      : [Form.coverage]

    steps[0] = { forms: coverageStepForms, navigation: false }
  } else {
    steps[0] = { forms: [Form.coverage], navigation: false }
  }

  steps[1] = { forms: [Form.customer] }

  steps[4] = {
    forms: [Form.identification, Form.address],
  }

  switch (RemoteConfigurationStore?.value?.ramoEnum) {
    case 'Auto':
      steps[1].forms.push(Form.vehicle)
      break
    case 'Viajes':
      steps[1].forms.push(Form.vehicle)
      break
    case 'Personas':
      steps[4].forms.push(Form.beneficiaries)
      break
    case 'Hogar':
      switch (RemoteConfigurationStore?.value.subRamo?.nombre) {
        case SubBranches.garantiaDeElectrodomesticos: {
          steps[3] = { forms: [Form.device] }
          break
        }
        case SubBranches.incendioVoluntario:
          steps[3] = { forms: [Form.realState] }
          break
      }
      break
    case 'Mascotas':
      steps[3] = { forms: [Form.pet] }
      break
  }

  steps[5] = {
    forms: [Form.resume, Form.paymentInfo, Form.aditionalInfo, Form.tos],
  }

  return steps
}

/**
 * Retrieves and applies the form configuration.
 *
 * It returns the current form configuration and the active step index.
 * It also provides a function to navigate through the steps of the stepper.
 *
 * @returns {{
 *   applyConfiguration: () => void,
 *   formConfig: Ref<FormConfig>,
 *   activeStep: Ref<number>,
 *   continueToStep: (param?: 'next' | 'previous') => void,
 *   buildForm: () => Record<string, Record<string, unknown>>
 * }}
 */
export const useFormBuilder = () => {
  const formConfig = ref<FormConfig>({})
  const activeStep = ref<number>(0)

  /**
   * Fetches and applies the form configuration.
   *
   * This function retrieves the form configuration object by making an asynchronous request
   * and assigns it to the `formConfig` reactive state. It also sets the `activeStep` to the minimum
   * step index found in the form configuration. If the form configuration is empty, the active step
   * remains at 0.
   */
  const applyConfiguration = () => {
    formConfig.value = getFormConfiguration()

    const stepsIndex = Object.keys(formConfig.value)
    if (stepsIndex.length > 0)
      activeStep.value = Math.min(...Object.keys(formConfig.value).map((step) => +step))
  }

  applyConfiguration()

  /**
   * Navigates through the steps of the stepper.
   * @param param - If is 'next' the stepper will navigate to the next step, if is 'previous' it will navigate to the previous step.
   * The default value is 'next'.
   */
  const continueToStep = (param: 'next' | 'previous' = 'next') => {
    const offset = param === 'previous' ? -1 : 1
    const stepsList = Object.keys(formConfig.value)
    const index = stepsList.indexOf(activeStep.value.toString())
    const step = stepsList[index + offset]
    if (step) activeStep.value = +step
  }

  const isLastStep = computed(() => {
    const stepsList = Object.keys(formConfig.value)
    const lasteStep = stepsList[stepsList.length - 1]
    return lasteStep === activeStep.value.toString()
  })

  return {
    applyConfiguration,
    formConfig,
    activeStep,
    continueToStep,
    buildForm,
    isLastStep,
  }
}
